import * as React from 'react';
import { Button, useNotification } from '@vechaiui/react';
import { Form } from 'formik';
import { useLocalization } from '@fluent/react';
import { useMutation } from '@apollo/client';

import Dialog from '#/components/controls/Dialog';
import Formik from '#/components/forms/Formik';
import LogisticsSignupForm from '#/components/forms/LogisticsSignupForm';
import SignUpLayout from '#/layouts/SignupLayout';
import yup from '#/libs/yup';
import { ApiErrorBlock, ErrorBlockFormik } from '#/components/forms/ErrorBlock';
import { CreateLogisticsDocument, CreateVehicleDocument } from '#/__generated__/graphql/graphql';
import { LOGISTICS_INITIAL_VALUES } from '#/constants/form-initial-values';
import { LogisticsFormData, sanitizeLogisticsFormData } from '#/utils/sanitize-data';
import { logisticsSchema } from '#/constants/validation-schema';

const LogisticsSignupPage = () => {
  const { l10n } = useLocalization();
  const notification = useNotification();
  const [createLogisticsMutation] = useMutation(CreateLogisticsDocument);
  const [createVehicleMutation] = useMutation(CreateVehicleDocument);
  const [apiErrors, setApiErrors] = React.useState<{ message: string }[] | undefined>();
  const [isSignUpDialogOpen, setSignUpDialogOpen] = React.useState(false);
  const [isSubmitting, setSubmitting] = React.useState(false);

  const createNewVehicles = async ($offers: { vehicleName: string }[]) => {
    const newVehicleResponses = await Promise.all(
      $offers.map((offer) =>
        createVehicleMutation({ variables: { input: { name: offer.vehicleName } } }),
      ) || [],
    );
    return newVehicleResponses.map((res) => res.data?.createVehicle?.node.id as string);
  };

  return (
    <SignUpLayout>
      <Formik
        initialValues={LOGISTICS_INITIAL_VALUES}
        onSubmit={async (formData, { resetForm }) => {
          setSubmitting(true);
          const newOffers = formData.offers.filter((offer) => offer.itemOfferedId !== 'other');
          if (formData.$offers) {
            const newVehicleIds = await createNewVehicles(formData.$offers.filter(Boolean));
            newOffers.push(
              ...formData.offers
                .filter((offer) => offer.itemOfferedId === 'other')
                .map((offer, index) => ({
                  ...offer,
                  itemOfferedId: newVehicleIds[index],
                })),
            );
          }
          delete formData.$offers;

          const form = sanitizeLogisticsFormData({
            ...formData,
            offers: newOffers,
          } as unknown as LogisticsFormData);
          try {
            await createLogisticsMutation({ variables: { input: form } });
            notification({
              title: 'Congratlations',
              description: 'Your form has been succuessfully submitted.',
              status: 'success',
              position: 'bottom-right',
            });
            setSubmitting(false);
            resetForm();
          } catch (err: any) {
            if (err.errors) {
              setApiErrors(err.errors);
            } else if (err.networkError?.result.errors) {
              setApiErrors(err.networkError.result.errors);
            } else {
              setApiErrors([err]);
            }
          }
        }}
        validationSchema={yup.object(logisticsSchema)}
      >
        {(formikProps) => (
          <>
            <Form>
              <div className="px-6 pb-8 pt-12">
                <div className="max-w-lg mx-auto">
                  <header className="mb-8">
                    <h2 className="text-3xl font-bold">
                      {l10n.getString('transporter')} {l10n.getString('sign-up')}
                    </h2>
                  </header>

                  <LogisticsSignupForm />
                </div>
              </div>

              <footer className="bg-white pt-8 px-6">
                <div className="max-w-lg mx-auto">
                  <div className="space-y-6">
                    {!formikProps.isValid && formikProps.submitCount > 0 && <ErrorBlockFormik />}
                    {apiErrors && <ApiErrorBlock errors={apiErrors} />}

                    <Button
                      className="w-full"
                      color="primary"
                      size="lg"
                      type="button"
                      variant="solid"
                      loading={isSubmitting}
                      onClick={() => {
                        setSignUpDialogOpen(true);
                      }}
                    >
                      {l10n.getString('sign-up')}
                    </Button>
                  </div>

                  <div className="border-t flex justify-between pt-1 pb-2 mt-8">
                    <div>
                      <span className="text-sm uppercase font-bold">
                        {l10n.getString('launching-soon')}
                      </span>
                    </div>

                    <div>
                      <span className="text-sm uppercase font-bold">
                        {l10n.getString('launching-date')}
                      </span>
                    </div>
                  </div>
                </div>
              </footer>
            </Form>

            {isSignUpDialogOpen && (
              <Dialog
                onConfirm={async () => {
                  await formikProps.validateForm();
                  setSignUpDialogOpen(false);
                  formikProps.submitForm();
                }}
                onClose={() => setSignUpDialogOpen(false)}
                isOpen={isSignUpDialogOpen}
                title={`${l10n.getString('confirm')}?`}
              >
                <div className="text-lg">
                  {`${l10n.getString('confirm')} ${l10n.getString('sign-up')} ${l10n.getString(
                    'transporter',
                  )}?`}
                </div>
              </Dialog>
            )}
          </>
        )}
      </Formik>
    </SignUpLayout>
  );
};

export default LogisticsSignupPage;
